/*
 * Full-page takeover when a countdown hits zero or an alarm fires.
 * Soft pulsing accent backdrop, big name, very large Stop button so a
 * teacher can dismiss it from across the room with one tap.
 */

.takeover {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: rgba(8, 18, 12, 0.55);
  backdrop-filter: blur(8px) saturate(140%);
  -webkit-backdrop-filter: blur(8px) saturate(140%);
  animation: fade-in 220ms ease;
}

.card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 48px clamp(40px, 6vw, 80px);
  max-width: min(720px, 92vw);
  background: var(--token-card-bg);
  border: 2px solid var(--token-accent);
  border-radius: 28px;
  box-shadow: 0 40px 80px -20px rgba(8, 18, 12, 0.5);
  text-align: center;
  animation: bounce-in 380ms cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Accent halo that pulses while the takeover is up — keeps it
 * "alive-feeling" for kids and visible from across the room. */
.card::before {
  content: '';
  position: absolute;
  inset: -8px;
  border-radius: 32px;
  border: 3px solid var(--token-accent);
  opacity: 0.55;
  animation: pulse-ring 1.4s ease-out infinite;
  pointer-events: none;
  z-index: -1;
}

.eyebrow {
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--token-accent);
  margin: 0;
}

.glyph {
  font-size: clamp(56px, 7vw, 96px);
  line-height: 1;
  margin: 0;
  filter: drop-shadow(0 4px 8px color-mix(in srgb, var(--token-accent) 40%, transparent));
}

.name {
  font-family: var(--token-font-display);
  font-size: clamp(36px, 4.4vw, 72px);
  line-height: 1.1;
  color: var(--token-text-primary);
  margin: 0;
  word-wrap: break-word;
  max-width: 100%;
}

.stopButton {
  font-family: var(--token-font-ui);
  font-size: clamp(18px, 1.6vw, 22px);
  font-weight: 700;
  letter-spacing: 0.6px;
  padding: 16px 48px;
  border-radius: 999px;
  border: 0;
  background: var(--token-accent);
  color: #fff;
  cursor: pointer;
  box-shadow:
    0 12px 28px -10px rgba(190, 110, 35, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  transition:
    background 120ms ease,
    transform 120ms ease;
  margin-top: 12px;
}

.stopButton:hover {
  background: color-mix(in srgb, var(--token-accent) 85%, #000);
  transform: translateY(-1px);
}

.stopButton:active {
  transform: translateY(0);
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes bounce-in {
  0% {
    transform: scale(0.85);
    opacity: 0;
  }
  60% {
    transform: scale(1.02);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.55;
  }
  70% {
    transform: scale(1.05);
    opacity: 0;
  }
  100% {
    transform: scale(1.05);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .card,
  .takeover {
    animation: none;
  }
  .card::before {
    animation: none;
    opacity: 0.3;
  }
}
